import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LocalizedLink from './LocalizedLink';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import { ArrowLeftRight, ArrowLeft } from 'lucide-react';
import SEOHead, { generateConverterStructuredData } from './SEOHead';
import {
  temperatureUnits,
  getUnitById,
  convertTemperature,
  generateSEODescription,
  popularTemperatureConversions,
  getConversionFormula,
} from '../data/temperatureRoutes';

const TemperatureConversionPage = () => {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLocalizedPath();
  const { conversion } = useParams();
  const [fromValue, setFromValue] = useState('0');
  const [toValue, setToValue] = useState('');

  // Parse conversion slug (e.g., "celsius-to-fahrenheit")
  const parseConversion = (slug) => {
    if (!slug) return { fromId: null, toId: null };
    const parts = slug.split('-to-');
    if (parts.length !== 2) return { fromId: null, toId: null };
    return { fromId: parts[0], toId: parts[1] };
  };

  const { fromId, toId } = parseConversion(conversion);
  const fromUnit = getUnitById(fromId);
  const toUnit = getUnitById(toId);

  useEffect(() => {
    if (fromUnit && toUnit && fromValue !== '') {
      const numValue = parseFloat(fromValue);
      if (!isNaN(numValue)) {
        const result = convertTemperature(numValue, fromUnit.id, toUnit.id);
        if (result !== null) {
          // Format the result
          if (Math.abs(result) < 0.000001 || Math.abs(result) > 1000000000) {
            setToValue(result.toExponential(6));
          } else {
            setToValue(parseFloat(result.toPrecision(10)).toString());
          }
        }
      }
    }
  }, [fromValue, fromUnit, toUnit]);

  const handleSwap = () => {
    if (fromUnit && toUnit) {
      window.location.href = `/convert/temperature/${toUnit.id}-to-${fromUnit.id}`;
    }
  };

  // Get related conversions (same from unit, different to units)
  const relatedConversions = temperatureUnits
    .filter((u) => u.id !== fromId && u.id !== toId)
    .map((u) => ({
      from: fromId,
      to: u.id,
      fromPlural: fromUnit?.plural || fromUnit?.name || '',
      toPlural: u.plural || u.name,
      toId: u.id,
    }));

  // Get reverse and related popular conversions
  const otherPopularConversions = popularTemperatureConversions
    .filter((c) => !(c.from === fromId && c.to === toId))
    .slice(0, 8);

  // Generate structured data for SEO (must be before early return for hooks rules)
  const structuredData = useMemo(() => {
    if (!fromUnit || !toUnit) return null;
    return generateConverterStructuredData(fromUnit, toUnit, 'temperature');
  }, [fromUnit, toUnit]);

  // Generate keywords for this specific conversion
  const keywords = fromUnit && toUnit
    ? `${fromUnit.name} to ${toUnit.name}, convert ${fromUnit.symbol} to ${toUnit.symbol}, ${fromUnit.name} ${toUnit.name} converter, temperature converter, ${fromUnit.symbol} to ${toUnit.symbol} formula`
    : 'temperature converter, unit converter';

  if (!fromUnit || !toUnit) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <LocalizedLink
          to="/convert/temperature"
          className="flex items-center gap-2 text-[#2563eb] hover:text-[#1d4ed8] mb-4"
        >
          <ArrowLeft size={18} />
          {t('common.backToConverter', { category: t('sidebar.converters.temperature') })}
        </LocalizedLink>
        <p className="text-gray-600">{t('common.invalidConversion')}</p>
      </div>
    );
  }

  const pageTitle = t('common.convertXToY', { from: t(`temperatureUnits.${fromUnit.id}.name`, fromUnit.name), to: t(`temperatureUnits.${toUnit.id}.name`, toUnit.name) });
  const pageDescription = generateSEODescription(fromUnit, toUnit);
  const formulaText = getConversionFormula(fromId, toId);

  return (
    <div className="space-y-4">
      <SEOHead
        title={t('seo.convertTitle', { from: t(`temperatureUnits.${fromUnit.id}.name`, fromUnit.name), to: t(`temperatureUnits.${toUnit.id}.name`, toUnit.name) })}
        description={pageDescription}
        keywords={keywords}
        structuredData={structuredData}
      />

      <LocalizedLink
        to="/convert/temperature"
        className="flex items-center gap-2 text-[#2563eb] hover:text-[#1d4ed8]"
      >
        <ArrowLeft size={18} />
        {t('common.backToConverter', { category: t('sidebar.converters.temperature') })}
      </LocalizedLink>

      {/* Main Converter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#4a7c9b] to-[#5a8caa] px-4 py-3">
          <h1 className="text-lg font-semibold text-white">{pageTitle}</h1>
        </div>

        <div className="p-6">
          <div className="grid md:grid-cols-[1fr,auto,1fr] gap-6 items-end">
            {/* From Section */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">{t(`temperatureUnits.${fromUnit.id}.name`, fromUnit.name)} ({fromUnit.symbol})
              </label>
              <input
                type="number"
                value={fromValue}
                onChange={(e) => setFromValue(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent text-lg"
                placeholder={t('common.enterValue')}
              />
            </div>

            {/* Swap Button */}
            <div className="flex justify-center">
              <button
                onClick={handleSwap}
                className="p-3 rounded-full bg-[#4a7c9b] text-white hover:bg-[#3d6a86] transition-colors shadow-md"
                title={t('common.swap')}
              >
                <ArrowLeftRight size={24} />
              </button>
            </div>

            {/* To Section */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">{t(`temperatureUnits.${toUnit.id}.name`, toUnit.name)} ({toUnit.symbol})
              </label>
              <input
                type="text"
                value={toValue}
                readOnly
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 text-lg"
                placeholder={t('common.result')}
              />
            </div>
          </div>

          {/* Result Display */}
          {fromValue !== '' && toValue && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-center text-lg text-gray-700">
                <span className="font-semibold">{fromValue}</span>{' '}
                <span className="text-gray-500">{fromUnit.symbol}</span> ={' '}
                <span className="font-semibold text-[#4a7c9b] text-xl">{toValue}</span>{' '}
                <span className="text-gray-500">{toUnit.symbol}</span>
              </p>
            </div>
          )}
        </div>

        {/* Conversion Formula */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">{t('common.formula')}</h2>
          <p className="text-sm text-gray-600 font-mono bg-white px-3 py-2 rounded border border-gray-200 inline-block">
            {formulaText}
          </p>
        </div>

        {/* Quick Reference Table */}
        <div className="border-t border-gray-200 p-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">{t('common.quickReference')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left font-medium text-gray-600">
                    {fromUnit.name} ({fromUnit.symbol})
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">
                    {toUnit.name} ({toUnit.symbol})
                  </th>
                </tr>
              </thead>
              <tbody>
                {[-40, -20, 0, 10, 20, 30, 37, 50, 100, 212].map((val) => {
                  const result = convertTemperature(val, fromUnit.id, toUnit.id);
                  const formattedResult =
                    Math.abs(result) < 0.000001 || Math.abs(result) > 1000000000
                      ? result.toExponential(4)
                      : parseFloat(result.toPrecision(8)).toString();
                  return (
                    <tr key={val} className="border-t border-gray-100">
                      <td className="px-3 py-2">
                        {val}{fromUnit.symbol}
                      </td>
                      <td className="px-3 py-2 text-[#4a7c9b] font-medium">
                        {formattedResult}{toUnit.symbol}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Related Conversions */}
      
      {/* About This Conversion Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-800">{t('common.unitDescriptionTitle')}</h2>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-1">{t('common.fromUnitLabel', { unit: t(`temperatureUnits.${fromUnit.id}.name`, fromUnit.name) })}</h3>
            <p className="text-sm text-gray-600">{t(`temperatureUnits.${fromUnit.id}.description`, t('common.noDescription'))}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-1">{t('common.toUnitLabel', { unit: t(`temperatureUnits.${toUnit.id}.name`, toUnit.name) })}</h3>
            <p className="text-sm text-gray-600">{t(`temperatureUnits.${toUnit.id}.description`, t('common.noDescription'))}</p>
          </div>
          <p className="text-sm text-gray-500 italic">{t('common.conversionNote', { from: t(`temperatureUnits.${fromUnit.id}.plural`, fromUnit.plural), to: t(`temperatureUnits.${toUnit.id}.plural`, toUnit.plural) })}</p>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-800">{t('common.relatedConversions', { unit: t(`temperatureUnits.${fromUnit.id}.name`, fromUnit.name) })}</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {relatedConversions.map((conv, idx) => (
              <LocalizedLink
                key={idx}
                to={`/convert/temperature/${conv.from}-to-${conv.to}`}
                className="text-sm text-gray-600 hover:text-[#2563eb] transition-colors py-1"
              >
                {t('common.xToY', { from: t(`temperatureUnits.${conv.from}.plural`, conv.fromPlural), to: t(`temperatureUnits.${conv.toId}.plural`, conv.toPlural) })}
              </LocalizedLink>
            ))}
          </div>
        </div>
      </div>

      {/* Other Popular Conversions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-800">{t('common.otherPopularConversions', { category: t('sidebar.converters.temperature') })}</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {otherPopularConversions.map((conv, idx) => {
              const from = getUnitById(conv.from);
              const to = getUnitById(conv.to);
              if (!from || !to) return null;
              return (
                <LocalizedLink
                  key={idx}
                  to={`/convert/temperature/${conv.from}-to-${conv.to}`}
                  className="text-sm text-gray-600 hover:text-[#2563eb] transition-colors py-1"
                >
                  {from.name} to {to.name}
                </LocalizedLink>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemperatureConversionPage;
